import { motion } from "framer-motion";
import { Pen, BarChart2, Repeat, ArrowRight } from "lucide-react";
import SectionContainer from "../components/SectionContainer";

const TRAITS = [
  {
    icon: Pen,
    title: "Your writing style",
    body: "Shadow drafts follow-ups that sound like you wrote them — because it learned from everything you've actually sent.",
  },
  {
    icon: Repeat,
    title: "Your workflow patterns",
    body: "Shadow learns which actions you take after specific call types and replicates that process automatically.",
  },
  {
    icon: BarChart2,
    title: "Your account priorities",
    body: "Shadow weights the intel, summaries, and next steps it surfaces based on what matters most to your pipeline.",
  },
];

export default function PersonalizationSection() {
  return (
    <SectionContainer id="personalization" className="py-20 sm:py-28">
      <div className="grid grid-cols-1 gap-14 lg:grid-cols-2 lg:gap-20 items-center">
        {/* Copy */}
        <div>
          <h2 className="text-[clamp(1.8rem,4vw,2.85rem)] font-bold tracking-tight text-neutral-900 mb-5">
            Shadow adapts to{" "}
            <span className="text-[#0092B4]">how each rep operates.</span>
          </h2>
          <p className="text-[15px] text-neutral-500 leading-relaxed mb-8">
            When every rep's follow-ups, briefs, and action items match their actual workflow, adoption
            stops being a problem. Shadow learns from each rep's calls, writing style, and patterns —
            not a generic template.
          </p>
        </div>

        {/* Trait cards */}
        <div className="space-y-4">
          {TRAITS.map((trait, i) => {
            const Icon = trait.icon;
            return (
              <motion.div
                key={trait.title}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.45, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="flex gap-4 rounded-2xl border border-neutral-100 bg-neutral-50/60 p-5"
              >
                <div className="h-10 w-10 rounded-xl bg-[#0092B4]/10 flex items-center justify-center shrink-0">
                  <Icon className="h-5 w-5 text-[#0092B4]" strokeWidth={2} />
                </div>
                <div>
                  <p className="text-[14px] font-semibold text-neutral-900 mb-1">{trait.title}</p>
                  <p className="text-[13px] text-neutral-500 leading-relaxed">{trait.body}</p>
                </div>
              </motion.div>
            );
          })}

          {/* "Getting smarter" note */}
          <div className="flex items-center gap-3 rounded-2xl border border-[#0092B4]/20 bg-[#0092B4]/5 px-5 py-4">
            <span className="relative flex h-2.5 w-2.5 shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#0092B4] opacity-60" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#0092B4]" />
            </span>
            <p className="text-[13px] text-[#0092B4] font-medium">
              Shadow improves with every call — no manual setup or re-training required.
            </p>
          </div>
        </div>
      </div>
    </SectionContainer>
  );
}
